import { ChevronLeft, Plus } from 'lucide-react';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { PageHead } from '@/admin-area/ui/PageHead';
import { PhonePrimary } from '@/admin-area/ui/PhonePrimary';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { ReportField } from '../capture/ReportField';
import { getTrip, listRoutes, listVehicles, type Trip, type Vehicle } from './api';
import { parseKm } from './calc';
import { RatePreview } from './RatePreview';
import { RoutePicker } from './RoutePicker';
import { emptyTripForm, formFromTrip, type TripForm } from './trip-form';
import { TripFields } from './TripFields';
import { useLoaded } from './use-loaded';
import { useRatePreview } from './use-rate-preview';
import { useTripSave, type TripProblem } from './use-trip-save';
import { VehicleForm } from './VehicleForm';

const today = () => new Date().toISOString().slice(0, 10);

/** The trip being edited, read once; `failed` when it cannot be read. */
function useTripToEdit(expenseId: string | undefined) {
  const [state, setState] = useState<
    { status: 'new' | 'loading' | 'failed' } | { status: 'ready'; trip: Trip }
  >({ status: expenseId === undefined ? 'new' : 'loading' });
  useEffect(() => {
    if (expenseId === undefined) return;
    let live = true;
    void getTrip(expenseId).then((outcome) => {
      if (live)
        setState(outcome.ok ? { status: 'ready', trip: outcome.value } : { status: 'failed' });
    });
    return () => {
      live = false;
    };
  }, [expenseId]);
  return state;
}

/**
 * «Nuovo viaggio» (release 3, spec §2.1) and its edit mode: date, route, the km typed by hand (the
 * map is release 4), vehicle, motivo and the «Nota» it goes in, with the rate and the amount the
 * server would compute shown while typing. No document is asked for.
 */
export function TripPage() {
  const { expenseId } = useParams();
  const loaded = useTripToEdit(expenseId);
  const { t } = useTranslation('expenses');
  if (loaded.status === 'loading') return <p role="status">{t('mileage.trip.loading')}</p>;
  if (loaded.status === 'failed') return <p role="alert">{t('mileage.trip.loadFailed')}</p>;
  const trip = loaded.status === 'ready' ? loaded.trip : null;
  if (trip !== null && !trip.can.edit) return <p role="alert">{t('mileage.trip.notEditable')}</p>;
  return <TripEditor trip={trip} />;
}

function TripEditor({ trip }: { readonly trip: Trip | null }) {
  const { t } = useTranslation('expenses');
  const navigate = useNavigate();
  const formId = useId();
  const [form, setForm] = useState<TripForm>(() =>
    trip === null ? emptyTripForm(today()) : formFromTrip(trip),
  );
  const set = (patch: Partial<TripForm>) => setForm((current) => ({ ...current, ...patch }));
  const vehicles = useLoaded(() => listVehicles());
  const routes = useLoaded(listRoutes);
  const preview = useRatePreview(form.date, form.vehicleId);
  const saving = useTripSave(trip);
  const [picking, setPicking] = useState(false);
  const [addingVehicle, setAddingVehicle] = useState(false);

  const blocked = preview.status === 'off';
  const submit = (
    <Button type="submit" form={formId} disabled={saving.busy || blocked}>
      {t('mileage.trip.save')}
    </Button>
  );
  return (
    <section aria-label={t('mileage.trip.title')} className="flex flex-col gap-4">
      <TripHead
        title={trip === null ? t('mileage.trip.title') : t('mileage.trip.editTitle')}
        submit={submit}
        onCancel={() => void navigate(-1)}
      />
      <form
        id={formId}
        className="flex max-w-3xl flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void saving.save(form);
        }}
      >
        <TripFields
          form={form}
          set={set}
          vehicles={vehicles.state.status === 'ready' ? vehicles.state.items : []}
          invalid={saving.invalidFor(form)}
          routesControl={<SheetButton onClick={() => setPicking(true)} text="routes" />}
          vehicleControl={<SheetButton onClick={() => setAddingVehicle(true)} text="vehicle" />}
        />
        <RatePreview state={preview} km={parseKm(form.km)} roundTrip={form.roundTrip} />
        <ReportField
          date={form.date}
          ownerUserId=""
          manages={false}
          value={form.reportId}
          onChange={(reportId) => set({ reportId })}
        />
        <TripExtras form={form} set={set} isNew={trip === null} problem={saving.problem} />
      </form>
      <PhonePrimary>{submit}</PhonePrimary>
      <RoutePicker
        open={picking}
        routes={routes.state.status === 'ready' ? routes.state.items : []}
        onClose={() => setPicking(false)}
        onPick={(route) => {
          set({ from: route.from, to: route.to, km: route.km.replace(/\.0$/, '') });
          setPicking(false);
        }}
      />
      <AddVehicleSheet
        open={addingVehicle}
        onOpenChange={setAddingVehicle}
        onSaved={(vehicle) => {
          vehicles.reload();
          set({ vehicleId: vehicle.id });
          setAddingVehicle(false);
        }}
      />
    </section>
  );
}

function AddVehicleSheet({
  open,
  onOpenChange,
  onSaved,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSaved: (vehicle: Vehicle) => void;
}) {
  const { t } = useTranslation('expenses');
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col gap-4 p-4">
        <SheetTitle>{t('mileage.vehicles.addTitle')}</SheetTitle>
        <SheetDescription>{t('mileage.vehicles.addHelp')}</SheetDescription>
        <VehicleForm searchAci={false} onCancel={() => onOpenChange(false)} onSaved={onSaved} />
      </SheetContent>
    </Sheet>
  );
}

/** Under the fields: keep the route, the tolls hint and the save's problem in words. */
function TripExtras({
  form,
  set,
  isNew,
  problem,
}: {
  readonly form: TripForm;
  readonly set: (patch: Partial<TripForm>) => void;
  readonly isNew: boolean;
  readonly problem: TripProblem | null;
}) {
  const { t } = useTranslation('expenses');
  return (
    <>
      {isNew ? (
        <label className="flex min-h-11 items-center gap-2 text-sm">
          <Checkbox
            checked={form.saveRoute}
            onChange={(event) => set({ saveRoute: event.target.checked })}
          />
          {t('mileage.trip.saveRoute')}
        </label>
      ) : null}
      <p className="text-muted-foreground text-sm">
        {t('mileage.trip.tolls')}{' '}
        <Link to="/expenses/new" className="underline">
          {t('mileage.trip.addExpense')}
        </Link>
      </p>
      {problem === null ? null : (
        <p role="alert" className="text-destructive text-sm">
          {t(`mileage.trip.problems.${problem}`)}
        </p>
      )}
    </>
  );
}

function TripHead({
  title,
  submit,
  onCancel,
}: {
  readonly title: string;
  readonly submit: ReactNode;
  readonly onCancel: () => void;
}) {
  const { t } = useTranslation('expenses');
  return (
    <>
      <button
        type="button"
        onClick={onCancel}
        className="text-muted-foreground hover:text-foreground -mb-2 inline-flex min-h-11 w-fit items-center gap-1 text-sm"
      >
        <ChevronLeft aria-hidden="true" className="size-4" />
        {t('mileage.trip.back')}
      </button>
      <PageHead
        title={title}
        summary={t('mileage.trip.summary')}
        actions={<span className="page-primary">{submit}</span>}
      />
    </>
  );
}

/** The button that opens «Percorsi usuali» or the add-vehicle sheet. */
function SheetButton({
  onClick,
  text,
}: {
  readonly onClick: () => void;
  readonly text: 'routes' | 'vehicle';
}) {
  const { t } = useTranslation('expenses');
  return text === 'routes' ? (
    <Button type="button" variant="outline" onClick={onClick}>
      {t('mileage.routes.title')}
    </Button>
  ) : (
    <Button type="button" variant="ghost" size="sm" onClick={onClick}>
      <Plus />
      {t('mileage.trip.addVehicle')}
    </Button>
  );
}
